import {
  createEthersRpcProvider,
  normalizeChainIdAsNumber,
} from '@avantarte/smart-contracts/utilities/createEthersRpcProvider';
import detectEthereumProvider from '@metamask/detect-provider';
import {
  EthereumClient,
  w3mConnectors,
  w3mProvider,
} from '@web3modal/ethereum';
import { useWeb3Modal, Web3Modal } from '@web3modal/react';
import {
  BigNumber,
  constants,
  getDefaultProvider,
  providers,
  utils,
} from 'ethers';
import React, { createContext, useCallback, useEffect } from 'react';
import { configureChains, createConfig, WagmiConfig } from 'wagmi';
import { goerli, mainnet, polygon, sepolia } from 'wagmi/chains';

import { csrClient } from '../../../apollo/api/avantArteClient';
import { SECOND } from '../../../constants/dateTime';
import AvantArteAccountGateway from '../../../gateways/AvantArteAccountGateway';
import { useEns } from '../../../hooks/web3/contracts/useEns';
import { useUpdatableAsyncState } from '../../../hooks/web3/useUpdatableAsyncState';
import { useAuth } from '../../../providers';
import { AuthState } from '../../../types/Auth';
import { EIP1193Provider } from '../../../types/web3/ethereumInterfaces';
import { ChainId } from '../../../types/web3/web3';
import {
  Web3ContextInterface,
  Web3LoginOptions,
  Web3ProviderType,
} from '../../../types/web3/web3Context';
import { trackWeb3Wallet } from '../../../utils/analytics';
import { captureError } from '../../../utils/capture';
import { delay } from '../../../utils/promises';
import { getErcChainId } from '../../../utils/web3/chains';
import {
  didUserCancelOperation,
  isWalletLocked,
} from '../../../utils/web3/web3Errors';
import { useWeb3Cache } from './useWeb3Cache';

const accountGateway = new AvantArteAccountGateway(csrClient);

const chains = [mainnet, polygon, goerli, sepolia];
const walletConnectProjectId =
  process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID!;

const { publicClient } = configureChains(chains, [
  w3mProvider({ projectId: walletConnectProjectId }),
]);
const wagmiConfig = createConfig({
  autoConnect: true,
  connectors: w3mConnectors({
    projectId: walletConnectProjectId,
    chains,
  }),
  publicClient,
});
const ethereumClient = new EthereumClient(wagmiConfig, chains);

export const defaultWeb3ContextValue: Web3ContextInterface = {
  isReady: false,
  isLoading: false,
  walletProvider: Web3ProviderType.METAMASK,
  authState: AuthState.CODE_PENDING,
  isMetamaskAvailable: false,
  login: () => {},
  logout: () => {},
  showConnectWalletDrawer: false,
  openConnectWalletDrawer: () => {},
  closeConnectWalletDrawer: () => {},
  nativeBalance: {
    fetch: () => Promise.resolve(constants.Zero),
    loading: false,
    settled: false,
    value: constants.Zero,
  },
  getProviderForChain: (c) => getDefaultProvider(c),
};

export const web3Context = createContext<Web3ContextInterface>(
  defaultWeb3ContextValue,
);

const Web3ContextProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const {
    chainId,
    ensAddress,
    isWeb3Connected,
    setChainId,
    setEnsAddress,
    setIsWeb3Connected,
    setIsWeb3ProviderLoaded,
    setShowConnectWalletDrawer,
    setSignatures,
    setWalletAddress,
    setWalletProvider,
    setWeb3Provider,
    showConnectWalletDrawer,
    signatures,
    walletAddress,
    walletProvider,
    web3Provider,
  } = useWeb3Cache();

  const ens = useEns(getErcChainId(chainId || '0x1')!);
  const { isLoggedIn, user } = useAuth();

  const requestTags = {
    walletAddress,
    walletProvider,
    accountId: user?.id,
    chainId,
  };

  const web3Modal = useWeb3Modal();

  const handleWalletChange = async (address?: string, chain?: string) => {
    const ercChainId = getErcChainId(chain as ChainId);
    setChainId(ercChainId);
    const lastAddress = walletAddress;
    setWalletAddress(address);
    if (!address) {
      return;
    }
    // track the wallet address
    trackWeb3Wallet(address, ercChainId!);
    // no need to update ENS if the address didn't change
    if (lastAddress === address) {
      return;
    }
    // update ENS name
    try {
      setEnsAddress(await ens.reverseResolution(address));
    } catch (err) {
      console.error('Failed resolving ENS', err);
    }
  };

  const disconnectWalletConnect = async () => {
    const acc = ethereumClient.getAccount();
    if (acc && acc.isConnected) {
      await ethereumClient.disconnect();
      window.sessionStorage.removeItem(
        'phantom.contentScript.providerInjectionOptions.v1',
      );
    }
  };

  const logout = async () => {
    setIsWeb3Connected(false);
    setWalletAddress(undefined);
    setSignatures({});
    setWeb3Provider(undefined);
    providerState.reset();
    if (walletProvider === Web3ProviderType.WALLET_CONNECT) {
      const acc = ethereumClient.getAccount();
      if (acc && acc.isConnected) {
        await disconnectWalletConnect();
      }
    }
  };

  const connectInjectedProvider = async () => {
    const detectedProvider =
      (await detectEthereumProvider()) as providers.ExternalProvider;
    const provider = new providers.Web3Provider(detectedProvider, 'any');
    await provider.send('eth_requestAccounts', []);
    return provider;
  };

  const connectWalletConnectProvider =
    async (): Promise<providers.Web3Provider> => {
      const acc = await ethereumClient.getAccount();
      if (acc && acc.isConnected) {
        const p = await acc.connector?.getProvider();
        return new providers.Web3Provider(p, 'any');
      }

      // otherwise it will cover the wallet connect modal
      setShowConnectWalletDrawer(false);
      if (acc) {
        await disconnectWalletConnect();
      }
      await web3Modal.open();

      return new Promise((resolve, reject) => {
        ethereumClient.watchAccount(async (acc) => {
          if (acc.status !== 'connected') {
            return;
          }

          try {
            await delay(SECOND);
            const p = await acc.connector?.getProvider();
            resolve(new providers.Web3Provider(p, 'any'));
            setShowConnectWalletDrawer(true);
          } catch (err) {
            reject(err);
          }
        });
      });
    };

  const isRealWalletError = (err: any) => {
    if (!err) {
      return false;
    }
    return !didUserCancelOperation(err) && !isWalletLocked(err);
  };

  const captureWalletCancellation = async <T extends any>(
    fn: () => Promise<T | undefined>,
  ) => {
    try {
      return await fn();
    } catch (err) {
      if (isRealWalletError(err)) {
        const message =
          err instanceof Error
            ? err.message
            : err
              ? String(err)
              : 'Wallet error';
        throw new Error(message, { cause: err });
      }
      return undefined;
    }
  };

  const connectWeb3Provider = async (web3ProviderType: Web3ProviderType) => {
    let provider: providers.Web3Provider | undefined;
    if (web3ProviderType === Web3ProviderType.METAMASK && window.ethereum) {
      provider = await connectInjectedProvider();
    } else if (web3ProviderType === Web3ProviderType.WALLET_CONNECT) {
      provider = await connectWalletConnectProvider();
    }

    if (provider) {
      const accounts = await provider.listAccounts();
      const network = await provider.getNetwork();
      const address = utils.getAddress(accounts[0]);
      handleWalletChange(address, String(network.chainId));
      return provider;
    }
    return undefined;
  };

  const providerState = useUpdatableAsyncState<
    providers.Web3Provider | undefined
  >({
    fn: async () => {
      if (web3Provider) {
        return web3Provider;
      }
      if (!isWeb3Connected || !isLoggedIn) {
        setWeb3Provider(undefined);
        return undefined;
      }
      setIsWeb3Connected(false);
      const ethersProvider = await connectWeb3Provider(walletProvider);
      if (!ethersProvider) {
        await logout();
        setWeb3Provider(undefined);
        return undefined;
      }
      const accounts = await ethersProvider.listAccounts();
      const address = utils.getAddress(accounts[0]);
      const signature = signatures[address];

      try {
        const isValid =
          signature &&
          (await accountGateway.web3VerifySignature(address, signature));
        if (isValid) {
          setIsWeb3Connected(true);
          setWeb3Provider(ethersProvider);
          return ethersProvider;
        }
      } catch (err) {
        captureError(err, { tags: requestTags });
        /**
         * failed to verify the signature
         * it means one of 3 things:
         *
         * 1. An API error happened, in this case the user can just try again when calling "login"
         * 2. Malformed data/signature, the user should go through the login flow again
         * 3. Another user is authenticated with this wallet, so the signature is not verified anymore,
         * the user should verify it again
         *
         * In all these cases, the error should be ignored and the signatures cache cleaned,
         * so the user can try and connect their wallet again
         */
      }
      await logout();
      setWeb3Provider(undefined);
      return undefined;
    },
    deps: [isLoggedIn],
    defaultValue: undefined,
    requestTags,
  });

  const login = async (loginOptions: Web3LoginOptions) => {
    await logout();
    providerState.updateState(async () => {
      if (!isLoggedIn) {
        setWeb3Provider(undefined);
        return undefined;
      }
      const ethersProvider = await connectWeb3Provider(loginOptions.provider);
      const accounts = await ethersProvider!.listAccounts();
      const address = utils.getAddress(accounts[0]);
      const signer = await ethersProvider?.getSigner();
      if (!signer) {
        setWeb3Provider(undefined);
        return undefined;
      }
      const nonce = await accountGateway.web3GenerateNonce(
        address,
        loginOptions.signingMessage,
      );
      const signature = await captureWalletCancellation(() =>
        signer.signMessage(nonce),
      );
      if (!signature) {
        closeConnectWalletDrawer();
        setWeb3Provider(undefined);
        return undefined;
      }
      const isValid = await accountGateway.web3VerifySignature(
        address,
        signature,
      );

      if (isValid) {
        setSignatures({
          ...signatures,
          [address]: signature,
        });
        setIsWeb3Connected(true);
        setWalletProvider(loginOptions.provider);
        setWeb3Provider(ethersProvider);

        setShowConnectWalletDrawer(false);
        return ethersProvider;
      }

      // login failed
      await logout();
      setWeb3Provider(undefined);
      return undefined;
    });
  };

  const nativeBalanceState = useUpdatableAsyncState<BigNumber>({
    fn: async () => {
      if (!web3Provider || !walletAddress) {
        throw new Error(
          'make sure wallet is connected before checking balance',
        );
      }
      const balance = await web3Provider.getBalance(walletAddress);
      return BigNumber.from(balance);
    },
    requirements: false, // never run this automatically
    defaultValue: constants.Zero,
    requestTags,
  });

  const hasMetamaskState = useUpdatableAsyncState<boolean>({
    fn: async () => {
      await detectEthereumProvider({
        mustBeMetaMask: true,
      });
      return true;
    },
    defaultValue: false,
    requestTags,
  });

  const authState = web3Provider
    ? AuthState.LOGGED_IN
    : providerState.loading
      ? AuthState.CODE_PENDING
      : AuthState.LOGGED_OUT;

  // set up listeners to wallet events (EIP1193)
  useEffect(() => {
    if (web3Provider) {
      const ethersProvider = web3Provider;
      const eip1193Provider = ethersProvider.provider as EIP1193Provider;

      const onAccountsChanged = async () => {
        await logout();
      };

      const onDisconnect = () => {
        logout();
      };

      const onChainChanged = async () => {
        const accounts = await ethersProvider.listAccounts();
        const address = utils.getAddress(accounts[0]);
        const network = await web3Provider.getNetwork();

        handleWalletChange(address, String(network.chainId));
      };

      eip1193Provider.on('accountsChanged', onAccountsChanged);
      eip1193Provider.on('disconnect', onDisconnect);
      eip1193Provider.on('chainChanged', onChainChanged);

      // Unsubscribe to events
      return () => {
        eip1193Provider.removeListener('accountsChanged', onAccountsChanged);
        eip1193Provider.removeListener('disconnect', onDisconnect);
        eip1193Provider.removeListener('chainChanged', onChainChanged);
      };
    }
    return () => {};
  }, [web3Provider, signatures]);

  // listen to when the aa account signs out
  const onAvantArteAccountSignOut = async () => {
    if (authState === AuthState.LOGGED_IN) {
      await logout();
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      onAvantArteAccountSignOut();
    }
  }, [isLoggedIn]);

  const openConnectWalletDrawer = () => {
    setShowConnectWalletDrawer(true);
  };

  const closeConnectWalletDrawer = () => setShowConnectWalletDrawer(false);

  const getProviderForChain = useCallback(
    (chainId: string | number): providers.Provider => {
      if (
        web3Provider &&
        BigNumber.from(chainId || constants.Zero).eq(chainId)
      ) {
        return web3Provider;
      }

      return createEthersRpcProvider({
        chainId: normalizeChainIdAsNumber(String(chainId)),
        infura: {
          projectId: process.env.NEXT_PUBLIC_INFURA_PROJECT_ID!,
        },
      });
    },
    [web3Provider, chainId],
  );

  useEffect(() => {
    setIsWeb3ProviderLoaded(true);
  }, []);

  const context = {
    isReady: providerState.settled,
    error: isRealWalletError(providerState.error)
      ? providerState.error
      : undefined,
    isLoading: providerState.loading,
    web3Provider,
    walletProvider,
    walletAddress,
    ensAddress,
    chainId,
    authState,
    isMetamaskAvailable: hasMetamaskState.value,
    login,
    logout,
    showConnectWalletDrawer,
    openConnectWalletDrawer,
    closeConnectWalletDrawer,
    nativeBalance: {
      ...nativeBalanceState,
      fetch: nativeBalanceState.retry,
    },
    getProviderForChain,
  };

  return (
    <>
      <Web3Modal
        ethereumClient={ethereumClient}
        projectId={walletConnectProjectId}
      />

      <WagmiConfig config={wagmiConfig}>
        <web3Context.Provider value={context}>{children}</web3Context.Provider>
      </WagmiConfig>
    </>
  );
};

export default Web3ContextProvider;
